import { Injectable, inject } from '@angular/core';
import {BaseService} from './base.service';
import {Logger} from '../logging/default-log.service';
import {ConfigurationHelper} from '../helper/configuration.helper';

/**
 * Service for redirecting to pages/routes using the correct iso language.
 * This can either be a default or given language or by getting the current language from the url.
 * This was needed because we changed the url structure of the shop mid development. This way
 * all Angular routes can stay as is and don't have to handle path variables and breaking tons of links.
 *
 * @author Patrick van Zadel
 */
@Injectable()
export class RedirectService extends BaseService {
	constructor() {
		const logger = inject(Logger);

		super(logger);
	}

	/**
	 * Redirect to the login page for the current language
	 */
	public redirectToLoginPageForCurrentLanguage() {
		window.location.href =
			this.getWebshopUrlForRelativePathWithCurrentLanguage('login');
	}

	/**
	 * Replace the 'iso' value in the given url with the chosen language.
	 * For example:
	 * If the current url is 'https://www.noviflora.com/en/products?page=1 and the chosen language is Swedish the new url becomes
	 * 'https://www.noviflora.com/sv/products?page=1
	 *
	 * @param location
	 * @param isoLang
	 */
	public replaceLanguageForGivenUrl(
		location: Location,
		isoLang: string
	): string {
		const regex = /^\/(\w+)\/(.+)/gm;
		const currentOrigin = location.origin;
		const currentPath = location.pathname;
		const currentUrlParams = location.search;

		const newLanguagePath = currentPath.replace(regex, `/${isoLang}/$2`);

		return `${currentOrigin}${newLanguagePath}${currentUrlParams}`;
	}

	public getWebshopUrlForRelativePathWithGivenLanguage(relativePath: string, isoLang: string) {
		return ConfigurationHelper.getWebshopUrl(`/${isoLang}${this.getPath(relativePath)}`);
	}

	public getWebshopUrlForRelativePath(relativePath: string) {
		return ConfigurationHelper.getWebshopUrl(`${this.getPath(relativePath)}`);
	}

	/**
	 * Get a relative CMS url with the current language in the url
	 *
	 * @param relativePath
	 */
	public getWebshopUrlForRelativePathWithCurrentLanguage(relativePath: string) {
		const currentIsoLang = window.location.pathname.split('/')[1];

		return ConfigurationHelper.getWebshopUrl(
			`/${currentIsoLang}${this.getPath(relativePath)}`
		);
	}

	private getPath(relativePath: string) {
		if (!relativePath.startsWith('/')) {
			relativePath = '/' + relativePath;
		}

		return relativePath;
	}
}
